// MetEn HOUSE FORWARD PATH — versioned house view of the annual GR DAM price
// level, used to pre-fill the Forward price curve (re-dispatch) projection mode.
//
// CONSTRUCTION (documented in BESS-APP docs/MetEn_House_Forward_Path.md):
//   Years covered by exchange anchors -> traded forward settlements
//     (EEX Greek Power Futures / HEnEx Energy Derivatives Market, baseload
//     calendar products), linearly interpolated across any gap years.
//   Years beyond the last anchor    -> geometric drift at
//     longRunDriftPctPerYear (house scenario assumption), floored at
//     terminalFloorEurMwh.
//   Low/High stress                 -> use the platform's Scenario deltas on
//     top of this central path (recommended: -15% / +15%).
//
// GOVERNANCE: whoever refreshes the anchors updates `anchorsEurMwh`,
// `asOfDate` and sets `status: "market"`. The as-of date and source are
// embedded in the level source label and flow into the PDF + teaser
// disclosure automatically. While status is "placeholder", the UI shows a
// warning and the label says so explicitly — placeholder numbers must never
// reach an external report.
//
// REFRESH PROCEDURE (approx. 2 minutes, monthly or before client deliveries):
//   1. EEX -> Market Data Hub -> Power Futures -> Greek Power (or HEnEx ->
//      Markets -> Derivatives Market daily report).
//   2. Read the settlement price of the next 2-3 Calendar (Cal) baseload
//      products (e.g. Cal-27, Cal-28, Cal-29), EUR/MWh.
//   3. Update anchorsEurMwh below, set asOfDate, set status to "market".

export const HOUSE_FORWARD_PATH = {
  version: "0.1",
  zone: "GR",
  // This static block is the OFFLINE FALLBACK; the "Load MetEn house path"
  // button prefers GET /market/house-forward-path, whose anchors are ingested
  // daily from EEX (FFBY for GR since 2026-09-17), with the HEnEx derivatives
  // Daily Official List kept as the fallback source and as a cross-check
  // quoted in the level-source label.
  status: "market",
  asOfDate: "2026-09-16",
  updatedBy: "MetEn Solutions — Energy/Market Analysis",
  anchorSourceName:
    "EEX Greek Power Futures (FFBY) — baseload Cal settlement prices (Market Data Hub)",
  // Calendar-year baseload anchors, EUR/MWh. EEX settlements, trading day
  // 2026-09-16. Mirror of BESS_feasibility/Configuration/house_forward_path.py
  // — keep the two in sync.
  //
  // Until 2026-09-17 this held ONE anchor (2027: 104.5, HEnEx quoted-orders
  // average) and 2028+ were the -1%/year drift assumption. EEX quotes three
  // Greek delivery years, so the path stops assuming the years the market
  // prices. The measured shape is -23.0% into 2028 where -1.0% was applied,
  // which makes this a correction rather than a refresh.
  anchorsEurMwh: {
    2027: 126.25,
    2028: 97.17,
    2029: 93.07,
  },
  // House scenario assumption beyond the last traded anchor: annual nominal
  // drift of the DAM baseload level. Rationale (see methodology doc): rising
  // RES penetration pressures the wholesale level, partially offset by demand
  // electrification and the CO2 cost floor on dispatchable generation.
  longRunDriftPctPerYear: -1.0,
  // Long-run nominal floor: the level is not drifted below the house view of
  // dispatchable-generation full cost (assumption, disclosed).
  terminalFloorEurMwh: 55.0,
  recommendedLowDeltaPct: -15,
  recommendedHighDeltaPct: 15,
};

export function houseForwardPathLabel() {
  const p = HOUSE_FORWARD_PATH;
  const flag = p.status === "market" ? "" : " — PLACEHOLDER anchors, refresh before external use";
  // No as-of date in the label: the report states what the platform does, not
  // when a figure was last refreshed. asOfDate stays as configuration.
  return `MetEn house forward path v${p.version} (${p.zone}), anchors: ${p.anchorSourceName}${flag}`;
}

// Build the annual EUR/MWh central path for `projectYears` project years.
// Project year 1 maps to `startCalendarYear` (default: next calendar year).
export function buildHouseForwardPath(projectYears, startCalendarYear) {
  const p = HOUSE_FORWARD_PATH;
  const years = Math.max(0, Number(projectYears) || 0);
  const startYear =
    Number.isInteger(Number(startCalendarYear)) && Number(startCalendarYear) >= 1900 && Number(startCalendarYear) <= 2200
      ? Number(startCalendarYear)
      : new Date().getFullYear() + 1;

  const anchorYears = Object.keys(p.anchorsEurMwh)
    .map(Number)
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => a - b);
  if (!anchorYears.length || years === 0) {
    return { levelsEurMwh: [], levelSource: houseForwardPathLabel(), isPlaceholder: p.status !== "market" };
  }
  const firstAnchor = anchorYears[0];
  const lastAnchor = anchorYears[anchorYears.length - 1];
  const drift = 1 + (Number(p.longRunDriftPctPerYear) || 0) / 100;
  const floor = Number(p.terminalFloorEurMwh) || 0;

  // A year before the first traded anchor has no quote: it stays blank, and a
  // leading blank is the simulation-year level (the backend mirror does the same).
  const levelForCalendarYear = (calYear) => {
    if (calYear < firstAnchor) return null;
    if (calYear === firstAnchor) return p.anchorsEurMwh[firstAnchor];
    if (calYear >= lastAnchor) {
      const drifted =
        p.anchorsEurMwh[lastAnchor] * Math.pow(drift, calYear - lastAnchor);
      return Math.max(floor, drifted);
    }
    // Interior year: linear interpolation between the surrounding anchors.
    let prev = firstAnchor;
    let next = lastAnchor;
    for (const y of anchorYears) {
      if (y <= calYear) prev = y;
      if (y >= calYear) { next = y; break; }
    }
    if (prev === next) return p.anchorsEurMwh[prev];
    const w = (calYear - prev) / (next - prev);
    return p.anchorsEurMwh[prev] + w * (p.anchorsEurMwh[next] - p.anchorsEurMwh[prev]);
  };

  const levelsEurMwh = [];
  for (let i = 0; i < years; i += 1) {
    const level = levelForCalendarYear(startYear + i);
    levelsEurMwh.push(level == null ? null : Math.round(level * 100) / 100);
  }
  return {
    levelsEurMwh,
    levelSource: houseForwardPathLabel(),
    isPlaceholder: p.status !== "market",
    startCalendarYear: startYear,
  };
}

// Leading years of a loaded house path that precede its first traded anchor:
// blank in a path loaded now, or the anchor's level copied back into them in a
// path loaded before. The study holds those years at the simulation-year level
// either way - mirror of
// BESS_feasibility/Configuration/forward_price_curves.house_path_years_before_first_anchor.
export function housePathYearsBeforeFirstAnchor(forwardCurve) {
  if (!String(forwardCurve?.levelSource || "").startsWith("MetEn house forward path")) return 0;
  const levels = Array.isArray(forwardCurve?.levelsEurMwh) ? forwardCurve.levelsEurMwh : [];
  const blank = (v) => v == null || v === "" || !Number.isFinite(Number(v));
  if (levels.length < 2) return 0;
  if (blank(levels[0])) {
    // A path loaded since the builder leaves those years blank.
    let lead = 0;
    while (lead < levels.length && blank(levels[lead])) lead += 1;
    return lead < levels.length ? lead : 0;
  }
  const first = Math.round(Number(levels[0]) * 1e6) / 1e6;
  let run = 1;
  while (run < levels.length && levels[run] != null && Math.round(Number(levels[run]) * 1e6) / 1e6 === first) run += 1;
  if (run < 2 || run >= levels.length) return 0;
  return run - 1;
}
